import { MarkOutcome, type Span } from '@wiztime/core';
import type { TFunction } from 'i18next';
import { toastInfo } from '../../shell/toastStore';
import { useDayUi, type SelectedGroupInfo } from './dayUiStore';
import { QUICK_MARK_LOCK_MS } from './quickMark';
import { beginQuickMarkWrite, endQuickMarkWrite } from './timeline/quickMarkGuard';

/** The write a parallel block needs: the day commands' `createBlock`, optionally selecting the new block. */
export type CreateParallel = (
  date: string,
  span: Span,
  projectId: string,
  extras?: { readonly select?: boolean },
) => Promise<MarkOutcome>;

/**
 * Add a block of `projectId` beside an existing group, spanning exactly the group (design
 * 2026-10-15 §1.2). Quick mark never replaces a block: the new block overlaps the group and the
 * lane draws it next to it. When the group already has the project nothing is written and the
 * user is told so. Returns the outcome; `Unchanged` is the "already this project" case.
 */
export async function addBesideGroup(
  group: Pick<SelectedGroupInfo, 'date' | 'span' | 'projectId'>,
  projectId: string,
  createBlock: CreateParallel,
  t: TFunction,
  options: { readonly select: boolean },
): Promise<MarkOutcome> {
  if (group.projectId === projectId) {
    toastInfo(t('quickMark.alreadyProject'));
    return MarkOutcome.Unchanged;
  }
  return createBlock(group.date, group.span, projectId, options.select ? { select: true } : undefined);
}

/**
 * A quick-mark tile was activated while the user has selected a block group (design 2026-10-15
 * §1.2): add a parallel block beside it instead of arming quick mark, and select the new block
 * automatically. A selection set automatically after a create (quick mark, tile drop, drag
 * create, parallel add) does not count: the tile then only arms quick mark. Returns false,
 * doing nothing, when no group is explicitly selected or quick mark is already active (the tile
 * then follows the brush's own transitions); true when the activation was consumed.
 */
export function markBesideSelection(projectId: string, createBlock: CreateParallel, t: TFunction): boolean {
  const ui = useDayUi.getState();
  const selected = ui.selectedGroup;
  if (
    selected === null ||
    !selected.explicit ||
    ui.selectedKey !== selected.key ||
    ui.quickMark.projectId !== null
  ) {
    return false;
  }
  // A second activation while the write is in flight (double click) is swallowed, not queued.
  if (!beginQuickMarkWrite()) return true;
  void addBesideGroup(selected, projectId, createBlock, t, { select: true }).finally(endQuickMarkWrite);
  return true;
}

/** The last click of a tile that `markBesideSelection` consumed: kept per tile by the component. */
export interface BesideMarkRecord {
  readonly projectId: string;
  readonly at: number;
}

/**
 * Click handling of a tile for the parallel add, including its double click (design 2026-10-15
 * §1.2): the second click of a double click (`detail >= 2` within the double-click window of a
 * consumed first click) is swallowed. Without it the first click's new block is already the
 * (automatic) selection, so the second click would arm Once or, when the selection were
 * explicit, report "already this project". Returns the new record and whether the click was
 * consumed; the caller stores the record.
 */
export function tileBesideClick(
  record: BesideMarkRecord | null,
  projectId: string,
  detail: number,
  createBlock: CreateParallel,
  t: TFunction,
  now: number = Date.now(),
): { readonly consumed: boolean; readonly record: BesideMarkRecord | null } {
  if (
    detail >= 2 &&
    record !== null &&
    record.projectId === projectId &&
    now - record.at <= QUICK_MARK_LOCK_MS
  ) {
    return { consumed: true, record };
  }
  if (markBesideSelection(projectId, createBlock, t)) {
    return { consumed: true, record: { projectId, at: now } };
  }
  return { consumed: false, record: null };
}
